import { useEffect, useMemo, useRef } from 'preact/hooks'
import { markResultPeekSeen } from 'libs/athos/helpers/peek'
import { loadDeferredImages } from 'helpers/deferred-images'
import { capSizes } from 'helpers/image-density'

export function generateSrcSet(url, widths) {
  return widths.map((width) => `${url}&width=${width} ${width}w`).join(', ')
}

const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

// The feed exports media in Shopify's media order, so a video (or a 3D model)
// can sit anywhere in `images` — including position 2, the hover/swipe slot.
// Those entries arrive either as the media file itself or as the poster frame
// Shopify serves from `/preview_images/`; neither should take an image slot.
const NON_IMAGE_MEDIA = /(\/videos?\/|\/preview_images\/|\.(mp4|m4v|mov|webm|ogv|m3u8|mpd|glb|usdz)(\?|$))/i

/**
 * Filter a result's media list down to the entries that can actually paint as
 * an `<img>`, preserving order. `[image, video, image]` therefore yields the
 * first and third items, so the hover (desktop) / swipe (mobile) slot always
 * gets the next real image rather than a broken video URL.
 */
export function getDisplayImages(images) {
  if (!Array.isArray(images)) return []
  return images.filter((image) => {
    const src = typeof image === 'string' ? image : image?.src
    return Boolean(src) && !NON_IMAGE_MEDIA.test(src)
  })
}

export function getLowQualityPlaceholder(imageUrl) {
  if (!imageUrl) return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII='
  return `${imageUrl}&width=5`
}

/**
 * The attributes that decide *when* an image is fetched.
 *
 * A priority image ships real `src`/`srcset`, so the browser starts fetching it
 * as soon as the card paints instead of waiting for LazyLoad to intersect it.
 * Everything else keeps the `lazy` + `data-*` pattern. Only ever mark the first
 * grid row priority — see `helpers/grid-priority`.
 */
function loadingProps(src, sizes, widths, priority) {
  if (priority) {
    return {
      src,
      srcset: generateSrcSet(src, widths),
      sizes: capSizes(sizes),
      loading: 'eager',
      fetchpriority: 'high'
    }
  }
  return {
    src: getLowQualityPlaceholder(src),
    'data-src': src,
    'data-srcset': generateSrcSet(src, widths),
    'data-sizes': sizes,
    loading: 'lazy'
  }
}

export function ResultImage({
  result,
  children,
  peek = false,
  insideCarousel = false,
  sizes = '50vw',
  widths = IMAGE_WIDTHS,
  priority = false
}) {
  const thumbRef = useRef(null)
  const scrollerRef = useRef(null)

  const [primaryImage, hoverImage] = useMemo(() => getDisplayImages(result?.images), [result?.images])

  // Below lg the swipe image sits edge-adjacent to the card, which LazyLoad counts as
  // in view, so it would decode for every card. Attach it on first swipe instead.
  const deferSwipeImage = !peek && !window.matchMedia('(min-width: 1024px)').matches

  useEffect(() => {
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update()
    }
  }, [result?.images, result?.image, deferSwipeImage])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || !deferSwipeImage) return

    // Already swiped (e.g. a swatch change remounted the image): no scroll will follow.
    if (scroller.scrollLeft > 0) {
      loadDeferredImages(scroller)
      return
    }

    const desktop = window.matchMedia('(min-width: 1024px)')
    const attach = (e) => {
      if (e.type === 'pointerenter' && e.pointerType !== 'mouse') return
      if (e.type === 'change' && !e.matches) return
      loadDeferredImages(scroller)
      detach()
    }
    const detach = () => {
      scroller.removeEventListener('scroll', attach)
      scroller.removeEventListener('pointerenter', attach)
      desktop.removeEventListener('change', attach)
    }
    scroller.addEventListener('scroll', attach, { passive: true })
    scroller.addEventListener('pointerenter', attach)
    desktop.addEventListener('change', attach)
    return detach
  }, [deferSwipeImage, hoverImage?.src])

  const handleScroll = (e) => {
    const el = e.currentTarget
    const thumb = thumbRef.current
    if (!thumb) return
    const max = el.scrollWidth - el.clientWidth
    const progress = max > 0 ? el.scrollLeft / max : 0
    thumb.style.transform = `translateX(${progress * 100}%)`
  }

  // Omitted from priority images so LazyLoad skips them entirely.
  const lazyClass = priority ? '' : 'lazy'

  return (
    <div className="relative size-full border-0 aspect-3/4 lg:overflow-hidden">
      {children}
      {primaryImage && hoverImage ? (
        <>
          <div
            ref={scrollerRef}
            onScroll={insideCarousel ? undefined : handleScroll}
            onAnimationEnd={peek ? (e) => { if (e.animationName === 'result-peek') markResultPeekSeen() } : undefined}
            className={`flex ${insideCarousel ? 'overflow-hidden' : 'overflow-x-auto overscroll-x-contain snap-x snap-mandatory'} touch-auto size-full lg:contents [&::-webkit-scrollbar]:hidden [scrollbar-width:none] ${peek ? 'children:animate-result-peek lg:children:animate-none motion-reduce:children:animate-none' : ''}`}
          >
            <img
              key={primaryImage.src}
              className={`snap-start shrink-0 ${lazyClass} w-full h-full object-cover object-bottom transition-all bg-[--section-bg-colour,theme(colors.brand.white)] lg:absolute lg:inset-0 lg:z-10 lg:group-hover/card:opacity-0`}
              {...loadingProps(primaryImage.src, sizes, widths, priority)}
              alt={primaryImage.alt || result.title || 'Product image'}
            />
            {/* The hover/swipe slot is never the LCP candidate, so it stays lazy even
                on a priority card, and takes `fetchpriority=low` so its bytes never
                compete with the first row. It still paints instantly on hover. */}
            <img
              key={hoverImage.src}
              className={`snap-start shrink-0 ${deferSwipeImage ? '' : 'lazy'} w-full h-full object-cover object-bottom bg-[--section-bg-colour,theme(colors.brand.white)] lg:absolute lg:inset-0`}
              {...loadingProps(hoverImage.src, sizes, widths, false)}
              fetchpriority="low"
              alt={hoverImage.alt || result.title || 'Product image 1'}
            />
          </div>
          <div className={`absolute inset-x-0 bottom-0 h-px overflow-hidden pointer-events-none lg:hidden ${insideCarousel ? 'hidden' : ''}`}>
            <div ref={thumbRef} className="h-full w-1/2 bg-[#101010]" />
          </div>
        </>
      ) : primaryImage?.src || result.image ? (
        // Only one usable image (or none in `images` at all) — no hover swap.
        // Prefer the filtered list so a video-first product never lands here
        // with the video as its single "image".
        <img
          key={primaryImage?.src ?? result.image}
          className={`${lazyClass} object-cover object-bottom size-full bg-[--section-bg-colour,theme(colors.brand.white)]`}
          {...loadingProps(primaryImage?.src ?? result.image, sizes, widths, priority)}
          alt={primaryImage?.alt || result.title || 'Product image'}
        />
      ) : null}
    </div>
  )
}
